/**
 * WebGL2 port of `mobile/src/components/onboarding/eigenfield-shader.ts` (SkSL).
 *
 * Same field, same recipe, same clock — the app runs it through Skia, the web
 * runs it through a raw WebGL2 context. Keeping them line-comparable matters:
 * `eigenfield-glsl.test.ts` diffs the uniform lists, and a uniform whose name
 * does not match the one the CPU sets is not an error, it silently reads zero.
 *
 * ── What differs from the SkSL, and why ──────────────────────────────────────
 *
 * 1. `gl_FragCoord.y` points UP. Skia's fragCoord points down. Everything here
 *    is authored in Skia's top-down frame, so the y axis is flipped once on the
 *    way in. Miss it and the whole field mirrors vertically — including
 *    `artFade`, which would then thin the lines at the top of the screen while
 *    the text at the bottom sat on full-strength art. That is mobile's worst
 *    shipped bug (gotchas.md #8) reintroduced by a sign.
 *
 * 2. Sampling is normalised. SkSL's `.eval(p)` takes PIXEL coordinates, which is
 *    why the original multiplies by `fieldSize`; `texture()` takes 0..1, so the
 *    multiply goes away and `fieldSize` survives only as documentation of the
 *    texture the LUT indexing assumes.
 *
 * 3. `highp` is mandatory, not stylistic. The field carries ~16 bits across two
 *    8-bit textures that are summed. `amp` is 30, so one 1/255 step moves a line
 *    by up to 13% of the gap to its neighbour — at mediump the entire field
 *    speckles. It will look fine on a desktop GPU and fall apart on a phone.
 *
 * 4. Derivatives stay manual. GLSL has `dFdx`/`fwidth`, which would replace
 *    three `phaseAt` evaluations with one, but the manual finite differences are
 *    what `verify_shader_parity.py` validates against the Python original.
 *    Optimise only after parity is green, as its own change.
 *
 * 5. `uvScale` is new. The recipe is authored for a 1080x1920 portrait frame; a
 *    desktop hero is landscape. Rather than stretch the field, the CPU passes a
 *    scale that crops it at the correct aspect. At (1,1) this is pixel-identical
 *    to the app.
 */

export const EIGENFIELD_VERT = `#version 300 es
// A single oversized triangle rather than a quad: no diagonal seam, one fewer
// vertex, and no vertex buffer needed — positions come from gl_VertexID.
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

export const EIGENFIELD_FRAG = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D fieldABC;      // modes 0,1,2 coarse, in R,G,B
uniform sampler2D fieldABCFine;  // their signed residuals, same layout
uniform sampler2D fieldD;        // mode 3: coarse in R, residual in G
uniform sampler2D lineLut;       // row 0: ink RGB, row 1: (duty, alpha, -)

uniform vec2 resolution;   // canvas size in CSS pixels
uniform float pixelRatio;  // so derivatives can be taken per DEVICE pixel
uniform vec2 fieldSize;
uniform vec2 refSize;

uniform vec4 weights;      // time-modulated per frame
uniform float freq;
uniform float amp;
uniform float warp;
uniform float rotFlag;
uniform float tiltCos;
uniform float tiltSin;
uniform float ramp0;
uniform float rampPow;

uniform float densityKnee;
uniform float densityFade;
uniform float densityFloor;
uniform float fadeFrac;
uniform float fadePow;

uniform float lutBase;
uniform float lutCount;
uniform vec3 ground;
uniform vec3 artFade;   // (bandStart, bandEnd, minAlpha) in screen fractions
uniform vec2 uvScale;   // design-frame crop; (1,1) reproduces the app exactly

out vec4 fragColor;

// The mode fields carry ~16 bits across two 8-bit textures. The split is additive,
// so the GPU's bilinear tap on each part sums to the bilinear tap on the original.
vec3 sampleABC(vec2 uv) {
  vec2 p = clamp(uv, 0.0, 1.0);
  vec3 coarse = texture(fieldABC, p).rgb * 2.0 - 1.0;
  vec3 fine = (texture(fieldABCFine, p).rgb - 0.5) * (2.0 / 255.0);
  return coarse + fine;
}

float sampleD(vec2 uv) {
  vec4 texel = texture(fieldD, clamp(uv, 0.0, 1.0));
  return (texel.r * 2.0 - 1.0) + (texel.g - 0.5) * (2.0 / 255.0);
}

float fieldAt(vec2 uv) {
  vec2 s = rotFlag > 0.5 ? uv.yx : uv;
  vec3 abc = sampleABC(s);
  float a = abc.r;
  float b = abc.g;
  float c = sampleABC(vec2(s.x + a * warp, s.y + b * warp)).b;
  float d = sampleD(vec2(s.x * 1.2 + 0.05, s.y * 0.9));
  return weights.x * a + weights.y * b + weights.z * c + weights.w * d;
}

vec2 applyTilt(vec2 uv) {
  vec2 c = uv - 0.5;
  return vec2(c.x * tiltCos - c.y * tiltSin, c.x * tiltSin + c.y * tiltCos) + 0.5;
}

// Screen space -> the design frame the recipe was authored in.
vec2 toDesign(vec2 screenUv) {
  return (screenUv - 0.5) * uvScale + 0.5;
}

float phaseAt(vec2 screenUv) {
  vec2 uv = applyTilt(toDesign(screenUv));
  float f = fieldAt(uv);
  float ampRamp = ramp0 + (1.0 - ramp0) * pow(clamp(uv.x, 0.0, 1.0), rampPow);
  return freq * uv.x + amp * ampRamp * f;
}

void main() {
  // Skia's fragCoord is top-down; gl_FragCoord is bottom-up. Flip once, here.
  vec2 screenUv = vec2(gl_FragCoord.x / resolution.x, 1.0 - gl_FragCoord.y / resolution.y);
  vec2 uv = applyTilt(toDesign(screenUv));

  float ph = phaseAt(screenUv);

  // Finite-difference derivatives, one device pixel apart. Without pixelRatio the
  // step would be 2-3x too wide and every line would over-blur on a retina screen.
  vec2 e = 1.0 / (resolution * pixelRatio);
  float dpx = phaseAt(screenUv + vec2(e.x, 0.0)) - ph;
  float dpy = phaseAt(screenUv + vec2(0.0, e.y)) - ph;

  // Local frequency drives the anti-aliasing width, so lines stay clean even where
  // the field compresses them.
  float aa = max(length(vec2(dpx, dpy)), 1e-6);

  // Per-line width / opacity / colour, keyed on the absolute line index so a strand
  // keeps its character as the field breathes.
  float lif = floor(ph);
  float lx = clamp(lif - lutBase + 0.5, 0.5, lutCount - 0.5);
  // Rows 0 and 1 of a 2-row texture: centres at t = 0.25 and 0.75. Sampled NEAREST —
  // filtering would blend one strand's width and colour into its neighbour's.
  vec4 inkTexel = texture(lineLut, vec2(lx / lutCount, 0.25));
  vec4 lineTexel = texture(lineLut, vec2(lx / lutCount, 0.75));
  float duty = lineTexel.r;
  float lineAlpha = lineTexel.g;

  float t = ph - lif;
  float d = min(t, duty - t);
  float line = clamp(d / aa + 0.5, 0.0, 1.0);
  line = line * line * (3.0 - 2.0 * line);
  line = line * lineAlpha;

  // Where the field bunches the lines they go muddy. Fade by local density so
  // crowded passages thin out on their own. The knee was measured per reference
  // pixel, so the derivatives are converted into those units first.
  vec2 dref = vec2(
    dpx * resolution.x * pixelRatio / refSize.x,
    dpy * resolution.y * pixelRatio / refSize.y
  );
  float over = max(length(dref) / densityKnee - 1.0, 0.0);
  line = line * clamp(1.0 - over * densityFade, densityFloor, 1.0);

  // Recipe's fade="top", in the tilted frame the generator used.
  line = line * pow(clamp(uv.y / fadeFrac, 0.0, 1.0), fadePow);

  // Screen-space band fade so text in the lower third lands on near-paper.
  // Evaluated here, per frame, in screen space — NOT baked into an image, which
  // would drift out of alignment the moment anything moved (gotchas.md #3).
  float band = smoothstep(artFade.x, artFade.y, screenUv.y);
  line = line * mix(1.0, artFade.z, band);

  vec3 col = ground + (inkTexel.rgb - ground) * line;
  fragColor = vec4(col, 1.0);
}
`;

/** Sampler uniforms, in the order the renderer must bind texture units. */
export const EIGENFIELD_SAMPLERS = ['fieldABC', 'fieldABCFine', 'fieldD', 'lineLut'] as const;
